import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useHolidays } from '@/hooks/useHolidays';
import { useAttendanceSettings } from '@/hooks/useAttendanceSettings';
import { Button } from '@/components/Button';
import { DateLabel } from '@/components/DateLabel';
import { Modal } from '@/components/Modal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { TimeRangeInput, type TimeRangeInputValue, computesCrossesMidnight } from '@/components/TimeRangeInput';
import { useShiftPatternPresets } from '@/hooks/useShiftPatternPresets';
import { buildLocalIso, isoToLocalHm, type PersonRef } from '@/lib/shift-format';
import { addDays } from '@/lib/calendar-day';
import { resolveAutoDeductBreakMinutes } from '@/lib/labor-hours';

/** 終了時刻が開始時刻以下 (日またぎ) の場合、終了側の日付を+1日する。ShiftBoardPage.tsx の
 *  既存パターンと同じ式 (`computesCrossesMidnight`) を使い、表示計算(自動控除プレビュー)と
 *  実際の保存(start_at/end_at)の両方でこの関数を通す — 2026-09-25、日またぎ勤務 (遅番18:00〜
 *  翌03:00等) で終了日を開始日と同じにしたまま計算していたため所要時間が負になり、休憩・実働と
 *  ともに0分になっていた実バグの修正 (§8-216 追記参照)。 */
const buildEndAtIso = (workDate: string, time: TimeRangeInputValue): string => {
  const endDate = computesCrossesMidnight(time) ? addDays(workDate, 1) : workDate;
  return buildLocalIso(endDate, time.endTime ?? '00:00');
};

/**
 * `work_records` (勤務実績) の作成・修正モーダル。
 * 元々 `ShiftRecordsPage.tsx` (`/shift/records`) 専用に実装されていたが、2026-09-09
 * (user Q1「日報でシフトを入れる場合もある」) により日報 (P-3、`DailyReportWorkShiftsSection`)
 * からも同じ `work_records` を作成できるようにするため、共通コンポーネントとして切り出した。
 * 業務ロジック (重複時間帯チェック・休憩自動控除・本人入力可否等) は `POST/PUT /work-records`
 * (`apps/api/src/routes/work-records.ts`) 側にあり、ここはそのUIのみを担う
 * (二重実装を避ける、§8-64/§8-70 型の乖離リスク回避)。
 */

export const STATUS_LABEL: Record<string, string> = {
  draft: '下書き', submitted: '承認待ち', approved: '承認済み', rejected: '却下',
};
// 語彙・配色は pages/requests/RequestsPage.tsx の同名ステータス (draft/submitted/approved/rejected)
// と揃える (R-3、二重定義を避ける)。
export const STATUS_CLS: Record<string, string> = {
  draft: 'bg-surface-sunken text-text-sub',
  submitted: 'bg-amber-50 text-amber-800',
  approved: 'bg-sky-50 text-sky-800',
  rejected: 'bg-zinc-100 text-zinc-700',
};

export function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${STATUS_CLS[status] ?? 'bg-surface-sunken text-text-sub'}`}>
      {STATUS_LABEL[status] ?? status}
    </span>
  );
}

export type WorkRecord = {
  work_record_id: string;
  employee_id: string | null; external_person_id: string | null;
  work_date: string; start_at: string; end_at: string; break_minutes: number;
  actual_labor_minutes: number; home_node_id: string; work_node_id: string;
  planned_assignment_id: string | null;
  record_kind: 'worked' | 'absence'; status: 'draft' | 'submitted' | 'approved' | 'rejected';
  reason_code_id: string | null; note: string | null;
  entered_by_user_id: string; row_version: number;
  submitted_at: string | null; approved_by_user_id: string | null; approved_at: string | null;
  display_name?: string | null;
  // §8-221対策: GET /work-records がサーバー側で解決したラベル (廃止/編集済みの理由コードでも
  // 参照可能、routes/work-records.ts resolveReasonCodeLabelMap 参照)。現行マスタ一覧
  // (`/masters/attendance-reason-codes`、廃止済みは含まれない) からの逆引きに頼ると、このモーダルの
  // Selectが空欄/不正表示になる (MonthlyAttendanceEntryPage.tsxで先に修正済みの表示バグと同根)。
  reason_code_label?: string | null;
};

export type ReasonCode = { reason_code_id: string; category: string; code: string; label: string };

export const workRecordPersonKey = (r: Pick<WorkRecord, 'employee_id' | 'external_person_id'>): string => (
  r.employee_id ? `E:${r.employee_id}` : `X:${r.external_person_id}`
);

// -----------------------------------------------------------------------------
// 記録モーダル (新規作成 / 修正、self・manager 共通)
// -----------------------------------------------------------------------------
export type WorkRecordModalTarget =
  | { mode: 'create'; workDate: string; plannedAssignmentId: string | null; homeNodeId: string; workNodeId: string; person: PersonRef | null; defaultStartAt: string; defaultEndAt: string }
  | { mode: 'edit'; record: WorkRecord };

export function WorkRecordModal({ target, onClose, onDone }: { target: WorkRecordModalTarget; onClose: () => void; onDone: () => void }) {
  const isEdit = target.mode === 'edit';
  const workDate = isEdit ? target.record.work_date : target.workDate;
  const nodeId = isEdit ? target.record.work_node_id : target.workNodeId;
  const [recordKind, setRecordKind] = useState<'worked' | 'absence'>(isEdit ? target.record.record_kind : 'worked');
  const [time, setTime] = useState<TimeRangeInputValue>({
    startTime: isoToLocalHm(isEdit ? target.record.start_at : target.defaultStartAt),
    endTime: isoToLocalHm(isEdit ? target.record.end_at : target.defaultEndAt),
  });
  const [breakMinutes, setBreakMinutes] = useState(String(isEdit ? target.record.break_minutes : 0));
  const [reasonCodeId, setReasonCodeId] = useState(isEdit ? (target.record.reason_code_id ?? '') : '');
  const [note, setNote] = useState(isEdit ? (target.record.note ?? '') : '');
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(null);
  const holidays = useHolidays(workDate, workDate);
  // 休憩自動控除 (attendance_settings.break_mode='auto_deduct'): 手入力欄は表示専用にし、
  // apps/api/src/lib/labor-hours.ts のミラー実装 (§8-134) で算出した値をそのまま保存する
  // (breakMode='clock' の店舗では従来通り手入力のまま一切変更しない)。
  const { data: attendanceSettings } = useAttendanceSettings(nodeId);
  const isAutoDeductBreak = attendanceSettings?.breakMode === 'auto_deduct';
  const autoBreakMinutes = useMemo(() => {
    if (!isAutoDeductBreak || !time.startTime || !time.endTime) return null;
    const startAt = buildLocalIso(workDate, time.startTime);
    const endAt = buildEndAtIso(workDate, time);
    const durationMinutes = Math.round((new Date(endAt).getTime() - new Date(startAt).getTime()) / 60000);
    return resolveAutoDeductBreakMinutes(durationMinutes, attendanceSettings?.breakRules ?? []);
  }, [isAutoDeductBreak, time.startTime, time.endTime, workDate, attendanceSettings]);
  const effectiveBreakMinutes = isAutoDeductBreak ? (autoBreakMinutes ?? 0) : (Number(breakMinutes) || 0);
  // 勤務パターンはあくまで開始・終了時刻の名前付きプリセット (§6-0 原則2、work_recordsに
  // shift_pattern_id列は無い)。選択すると time を上書きするだけで、保存するのは時刻のみ。
  const { presets: patternPresets } = useShiftPatternPresets(nodeId, recordKind !== 'absence');
  // design-expert指摘: 編集時、既存の時刻がプリセットと完全一致していても自動選択されず
  // 「未選択」表示になっていた (再認 > 想起、Nielsen H6)。プリセット一覧を取得できた時点で
  // 一度だけ一致確認する (保存値には影響しない表示専用の補助)。
  useEffect(() => {
    if (!selectedPresetId && patternPresets.length > 0) {
      const match = patternPresets.find((p) => p.startTime === time.startTime && p.endTime === time.endTime);
      if (match) setSelectedPresetId(match.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patternPresets]);
  // ShiftBoardPage/ShiftStandingPatternsPage と同型 (D-235): プリセット選択後に手動で時刻を
  // 変えても選択状態はクリアしない (「由来」を保持する既存方針)。代わりに逸脱を明示する。
  const selectedPreset = selectedPresetId ? patternPresets.find((p) => p.id === selectedPresetId) : undefined;
  const deviatesFromPattern = !!selectedPreset
    && (selectedPreset.startTime !== time.startTime || selectedPreset.endTime !== time.endTime);
  // user指摘 (2026-09-09): パターン選択時に休憩時間も既定値として反映する。手動で変更した後の
  // 再選択でも「パターン通り」に揃え直せるよう、選択のたびに上書きする (時刻と同じ挙動)。
  const handlePresetSelect = (presetId: string) => {
    setSelectedPresetId(presetId);
    // design-expert指摘: auto_deductモードでは休憩欄が表示専用(自動算出)のため、プリセットの
    // 休憩時間をbreakMinutes stateへ反映しても画面にも保存にも影響しない (dead state)。
    // 無駄な状態更新で将来の混乱を招かないよう、clockモードのときだけ反映する。
    const preset = patternPresets.find((p) => p.id === presetId);
    if (!isAutoDeductBreak && preset?.breakMinutes !== undefined) setBreakMinutes(String(preset.breakMinutes));
  };

  const reasonCategory = recordKind === 'absence' ? 'absence' : 'shift_change';
  const { data: reasonCodesData } = useQuery<{ reason_codes: ReasonCode[] }>({
    queryKey: ['attendance-reason-codes', reasonCategory],
    queryFn: () => apiFetch(`/masters/attendance-reason-codes?category=${reasonCategory}`),
  });
  const reasonCodes = reasonCodesData?.reason_codes ?? [];
  // §8-221対策: 廃止/編集済みの理由コード (現行マスタ一覧 `reasonCodes` に含まれない) を参照する
  // 既存レコードを開くと、Select の value がどの選択肢とも一致せず空欄/不正表示になっていた。
  // 現在の値をラベル付きで選択肢に追加して正しく表示する。**別の値へ変更する操作自体は既存の
  // 現行選択肢からのみ選べる制約のままで変更しない** (このレコード用に追加した1件だけの特例)。
  // design-expertレビュー指摘: 注記を末尾に付けるとSelectTriggerのtruncateでラベルが長い場合に
  // 見えなくなる。先頭に固定長の接頭辞を置くことで、切り詰められても必ず視認できるようにする。
  const editingReasonCodeId = isEdit ? target.record.reason_code_id : null;
  const editingReasonCodeLabel = isEdit ? target.record.reason_code_label : null;
  const selectableReasonCodes = useMemo(() => {
    if (!editingReasonCodeId) return reasonCodes;
    if (reasonCodes.some((r) => r.reason_code_id === editingReasonCodeId)) return reasonCodes;
    return [
      {
        reason_code_id: editingReasonCodeId, category: reasonCategory, code: '',
        label: `(廃止) ${editingReasonCodeLabel ?? '削除済みの理由コード'}`,
      },
      ...reasonCodes,
    ];
  }, [editingReasonCodeId, editingReasonCodeLabel, reasonCodes, reasonCategory]);

  const saveMut = useMutation({
    mutationFn: () => {
      if (isEdit) {
        return apiFetch(`/work-records/${target.record.work_record_id}`, {
          method: 'PUT',
          body: JSON.stringify({
            start_at: buildLocalIso(workDate, time.startTime ?? '00:00'),
            end_at: buildEndAtIso(workDate, time),
            break_minutes: effectiveBreakMinutes,
            reason_code_id: reasonCodeId || null,
            note: note.trim() || null,
            row_version: target.record.row_version,
          }),
        });
      }
      return apiFetch('/work-records', {
        method: 'POST',
        body: JSON.stringify({
          employee_id: target.person?.employee_id ?? null,
          external_person_id: target.person?.external_person_id ?? null,
          record_kind: recordKind,
          start_at: buildLocalIso(workDate, time.startTime ?? '00:00'),
          end_at: recordKind === 'absence' ? buildLocalIso(workDate, time.startTime ?? '00:00') : buildEndAtIso(workDate, time),
          break_minutes: recordKind === 'absence' ? 0 : effectiveBreakMinutes,
          home_node_id: target.homeNodeId, work_node_id: target.workNodeId,
          planned_assignment_id: target.plannedAssignmentId,
          reason_code_id: reasonCodeId || null,
          note: note.trim() || null,
        }),
      });
    },
    onSuccess: () => { toast.success(isEdit ? '実績を修正しました' : '実績を記録しました'); onDone(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  return (
    <Modal
      open title={isEdit ? '勤務実績を修正' : '勤務実績を記録'} onClose={onClose} size="sm"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" loading={saveMut.isPending} onClick={() => saveMut.mutate()}>保存する</Button>
        </>
      )}
    >
      <div className="space-y-3">
        <p className="text-xs text-text-sub">
          <DateLabel date={workDate} withWeekday holidays={holidays} />
          {target.mode === 'create' && target.plannedAssignmentId && (
            <> の予定（{isoToLocalHm(target.defaultStartAt)}〜{isoToLocalHm(target.defaultEndAt)}）と異なる点だけ記録してください。</>
          )}
          {target.mode === 'create' && !target.plannedAssignmentId && <> の実績を記録します（予定に無い勤務）。</>}
        </p>
        {!isEdit && (
          <div>
            <label className="block text-xs text-text-sub mb-1">区分</label>
            <Select value={recordKind} onValueChange={(v) => setRecordKind(v as typeof recordKind)}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="worked">勤務した</SelectItem>
                <SelectItem value="absence">欠勤（予定はあったが働かなかった）</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {recordKind !== 'absence' && (
          <>
            <TimeRangeInput
              value={time} onChange={setTime} legend="実際の勤務時間"
              presets={patternPresets} selectedPresetId={selectedPresetId}
              onPresetSelect={handlePresetSelect}
            />
            {deviatesFromPattern && (
              <p className="text-xs text-alert-text px-1.5 py-1 rounded bg-alert-bg">
                「{selectedPreset!.label}」パターン ({selectedPreset!.startTime}〜{selectedPreset!.endTime}) から時刻を変更しています。
              </p>
            )}
            <div className="w-full sm:w-56">
              <label id="break-minutes-label" className="block text-xs text-text-sub mb-1">実際の休憩 (分)</label>
              {isAutoDeductBreak ? (
                <div aria-labelledby="break-minutes-label" className="min-h-[44px] px-3 py-2 rounded border border-surface-border bg-surface-sunken text-text-body flex items-center text-sm tabular-nums">
                  {autoBreakMinutes ?? 0}分（自動計算）
                </div>
              ) : (
                <input type="number" min={0} value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)}
                  className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
              )}
            </div>
          </>
        )}
        {selectableReasonCodes.length > 0 && (
          <div>
            <label className="block text-xs text-text-sub mb-1">理由 (任意)</label>
            <Select value={reasonCodeId || undefined} onValueChange={setReasonCodeId}>
              <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="選ぶ" /></SelectTrigger>
              <SelectContent>{selectableReasonCodes.map((r) => <SelectItem key={r.reason_code_id} value={r.reason_code_id}>{r.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div>
          <label className="block text-xs text-text-sub mb-1">メモ (任意)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            className="w-full px-3 py-2 rounded border border-surface-border focus-ring text-sm" />
        </div>
      </div>
    </Modal>
  );
}
